import { useCallback, useEffect, useRef, useState } from 'react';

const BOTTOM_SLOP = 48;

// Follow layout growth, not tokens: Markdown, tables and asynchronous cards can
// change height independently. Only reader input may change following intent.
export default function useChatAutoScroll({ conversationId, active }) {
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const following = useRef(true);
  const frame = useRef(null);
  const lastTop = useRef(0);
  const [showJump, setShowJump] = useState(false);

  const followLayout = useCallback(() => {
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      const el = scrollRef.current;
      if (!el) return;
      if (following.current) {
        // One immediate position update per frame avoids competing smooth-scroll
        // animations during streaming and also respects reduced-motion settings.
        el.scrollTop = el.scrollHeight;
        lastTop.current = el.scrollTop;
      }
      setShowJump(!following.current && el.scrollHeight - el.clientHeight - el.scrollTop > BOTTOM_SLOP);
    });
  }, []);

  const jumpToLatest = useCallback(() => {
    following.current = true;
    setShowJump(false);
    followLayout();
  }, [followLayout]);

  useEffect(() => { jumpToLatest(); }, [conversationId, jumpToLatest]);
  // A submitted question starts a new reading position; updates within that
  // turn (including draft resets and final replacement) never reset intent.
  useEffect(() => { if (active) jumpToLatest(); }, [active, jumpToLatest]);

  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content) return undefined;
    let touchY = null;
    const pause = () => { following.current = false; followLayout(); };
    const onWheel = (event) => { if (event.deltaY < 0) pause(); };
    const onTouchStart = (event) => { touchY = event.touches[0]?.clientY ?? null; };
    const onTouchMove = (event) => {
      const nextY = event.touches[0]?.clientY;
      if (touchY !== null && nextY > touchY) pause();
      touchY = nextY ?? null;
    };
    const onKeyDown = (event) => {
      if (event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (['ArrowUp', 'PageUp', 'Home'].includes(event.key) || (event.key === ' ' && event.shiftKey)) pause();
    };
    const onScroll = () => {
      const top = el.scrollTop;
      const distance = el.scrollHeight - el.clientHeight - top;
      if (top < lastTop.current - 1 && distance > BOTTOM_SLOP) following.current = false;
      else if (top > lastTop.current + 1 && distance <= BOTTOM_SLOP) following.current = true;
      lastTop.current = top;
      setShowJump(!following.current && distance > BOTTOM_SLOP);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('keydown', onKeyDown);
    const observer = new ResizeObserver(followLayout);
    observer.observe(content);
    observer.observe(el);
    followLayout();
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('keydown', onKeyDown);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
    };
  }, [followLayout]);

  return { scrollRef, contentRef, showJump, jumpToLatest };
}
